import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  ExternalLink, Loader2, RefreshCw, ShoppingBag, Plus, X, Trash2, Edit2, Archive,
  Tag, Receipt, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall, formatCents, dollarsToCents } from '@/lib/api';

// ── Local types ──────────────────────────────────────────────────────────
interface Product {
  id: string;
  name: string;
  sku: string | null;
  description: string | null;
  price_cents: number;
  stock_qty: number;
  image_url: string | null;
  archived: number;
}

interface Sale {
  id: string;
  product_id: string;
  product_name: string;
  member_id: string | null;
  member_name: string | null;
  qty: number;
  unit_price_cents: number;
  total_cents: number;
  payment_method: 'cash' | 'card' | 'stripe' | 'other';
  buyer_name: string | null;
  sold_at: string;
}

interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  description: string;
  price_cents: number;
  currency: string;
  image_url: string | null;
  online_store_url: string;
}

type Tab = 'inventory' | 'sales' | 'shopify';

export default function Store() {
  const { getToken } = useAuth();
  const [tab, setTab] = useState<Tab>('inventory');
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [shopify, setShopify] = useState<{ items: ShopifyProduct[]; configured: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | 'new' | null>(null);
  const [sellFor, setSellFor] = useState<Product | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const [p, s, sh] = await Promise.all([
        apiCall<{ items: Product[] }>('/v1/products?include_archived=true', { bearerToken: token }),
        apiCall<{ items: Sale[] }>('/v1/store_sales?limit=200', { bearerToken: token }),
        apiCall<{ items: ShopifyProduct[]; configured: boolean }>('/v1/storefront/products', { bearerToken: token }),
      ]);
      setProducts(p.items); setSales(s.items); setShopify(sh);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  const activeProducts = products.filter((p) => !p.archived);
  const lowStock = activeProducts.filter((p) => p.stock_qty <= 3);
  const totalToday = sales
    .filter((s) => s.sold_at.slice(0, 10) === new Date().toISOString().slice(0, 10))
    .reduce((acc, s) => acc + s.total_cents, 0);

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Store</h1>
          <p className="text-sm text-muted-foreground">
            Front-desk POS for shirts, water, protein. Sales roll into Reports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
          </Button>
          {tab === 'inventory' && (
            <Button size="sm" onClick={() => setEditingProduct('new')}>
              <Plus className="mr-1 h-4 w-4" /> New product
            </Button>
          )}
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <StatCard label="Active products" value={String(activeProducts.length)} icon={<Tag className="h-4 w-4" />} />
        <StatCard label="Sales today" value={formatCents(totalToday)} icon={<Receipt className="h-4 w-4" />} />
        <StatCard label="Recent transactions" value={String(sales.length)} icon={<Receipt className="h-4 w-4" />} />
        <StatCard label="Low stock" value={String(lowStock.length)} tone={lowStock.length > 0 ? 'warn' : 'ok'} icon={<AlertTriangle className="h-4 w-4" />} />
      </div>

      {/* Tab strip */}
      <div className="border-b flex items-center gap-1">
        {(['inventory', 'sales', 'shopify'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={
              'px-3 py-2 text-sm border-b-2 -mb-px capitalize ' +
              (tab === t ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')
            }
          >
            {t === 'shopify' ? 'Shopify (optional)' : t}
          </button>
        ))}
      </div>

      {err && <Card><CardContent className="py-4 text-sm text-destructive">{err}</CardContent></Card>}

      {tab === 'inventory' && (
        <InventoryGrid
          products={products}
          onEdit={setEditingProduct}
          onSell={setSellFor}
          onArchive={async (id) => {
            const token = await getToken();
            await apiCall(`/v1/products/${id}`, { method: 'DELETE', bearerToken: token });
            void load();
          }}
          onUnarchive={async (id) => {
            const token = await getToken();
            await apiCall(`/v1/products/${id}`, { method: 'PATCH', bearerToken: token, body: { archived: false } });
            void load();
          }}
        />
      )}

      {tab === 'sales' && (
        <SalesList
          sales={sales}
          onRefund={async (id) => {
            if (!confirm('Refund this sale and restock the item?')) return;
            const token = await getToken();
            await apiCall(`/v1/store_sales/${id}`, { method: 'DELETE', bearerToken: token });
            void load();
          }}
        />
      )}

      {tab === 'shopify' && (
        <ShopifyPanel data={shopify} loading={loading} />
      )}

      {editingProduct && (
        <ProductEditor
          initial={editingProduct === 'new' ? null : editingProduct}
          onClose={() => setEditingProduct(null)}
          onSaved={() => { setEditingProduct(null); void load(); }}
        />
      )}
      {sellFor && (
        <SellDialog
          product={sellFor}
          onClose={() => setSellFor(null)}
          onSold={() => { setSellFor(null); void load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon, tone }: { label: string; value: string; icon: React.ReactNode; tone?: 'ok' | 'warn' }) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon} {label}</div>
        <div className={'text-2xl font-semibold mt-1 ' + (tone === 'warn' ? 'text-amber-600' : '')}>{value}</div>
      </CardContent>
    </Card>
  );
}

function InventoryGrid({
  products, onEdit, onSell, onArchive, onUnarchive,
}: {
  products: Product[];
  onEdit: (p: Product) => void;
  onSell: (p: Product) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
}) {
  if (products.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          <ShoppingBag className="mx-auto mb-3 h-8 w-8 opacity-40" />
          No products yet. Click "New product" to add inventory.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {products.map((p) => (
        <Card key={p.id} className={'overflow-hidden flex flex-col ' + (p.archived ? 'opacity-50' : '')}>
          {p.image_url && <div className="aspect-square bg-muted"><img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /></div>}
          <CardContent className="p-4 flex-1 flex flex-col space-y-2">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-medium leading-tight">{p.name}</h3>
              {p.archived ? <Badge variant="outline">Archived</Badge> : (
                <Badge variant={p.stock_qty <= 3 ? 'destructive' : 'outline'}>
                  {p.stock_qty} in stock
                </Badge>
              )}
            </div>
            {p.sku && <p className="text-xs text-muted-foreground font-mono">{p.sku}</p>}
            {p.description && <p className="text-xs text-muted-foreground line-clamp-2">{p.description}</p>}
            <div className="mt-auto flex items-center justify-between pt-2">
              <span className="text-base font-semibold tabular-nums">{formatCents(p.price_cents)}</span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(p)}>
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
                {p.archived ? (
                  <Button size="sm" variant="outline" className="h-7" onClick={() => onUnarchive(p.id)}>Restore</Button>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => onArchive(p.id)}>
                      <Archive className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" className="h-7" disabled={p.stock_qty === 0} onClick={() => onSell(p)}>
                      Sell
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SalesList({ sales, onRefund }: { sales: Sale[]; onRefund: (id: string) => void }) {
  if (sales.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
        <Receipt className="mx-auto mb-3 h-8 w-8 opacity-40" />
        No sales recorded yet.
      </CardContent></Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b">
            <tr>
              <th className="text-left px-4 py-2 font-medium">When</th>
              <th className="text-left px-4 py-2 font-medium">Product</th>
              <th className="text-left px-4 py-2 font-medium">Buyer</th>
              <th className="text-right px-4 py-2 font-medium">Qty</th>
              <th className="text-right px-4 py-2 font-medium">Total</th>
              <th className="text-left px-4 py-2 font-medium">Method</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sales.map((s) => (
              <tr key={s.id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">{new Date(s.sold_at).toLocaleString()}</td>
                <td className="px-4 py-2">{s.product_name}</td>
                <td className="px-4 py-2 text-muted-foreground">{s.member_name ?? s.buyer_name ?? '—'}</td>
                <td className="px-4 py-2 text-right tabular-nums">{s.qty}</td>
                <td className="px-4 py-2 text-right tabular-nums font-medium">{formatCents(s.total_cents)}</td>
                <td className="px-4 py-2"><Badge variant="outline" className="capitalize">{s.payment_method}</Badge></td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={() => onRefund(s.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ShopifyPanel({ data, loading }: { data: { items: ShopifyProduct[]; configured: boolean } | null; loading: boolean }) {
  if (loading && !data) return <Card><CardContent className="py-8 text-sm text-muted-foreground">Loading…</CardContent></Card>;
  if (!data?.configured) {
    return (
      <Card>
        <CardContent className="py-8 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <ShoppingBag className="h-5 w-5" />
            <span className="font-medium">Shopify isn't connected (optional).</span>
          </div>
          <p className="text-xs text-muted-foreground">
            The in-house POS above handles most gym storefronts. Hook up Shopify only if you also run an online
            storefront elsewhere — set these env vars on the api service:
          </p>
          <pre className="text-xs bg-muted rounded p-3 overflow-x-auto"><code>SHOPIFY_STORE_DOMAIN=your-store.myshopify.com
SHOPIFY_STOREFRONT_ACCESS_TOKEN=…</code></pre>
        </CardContent>
      </Card>
    );
  }
  if (data.items.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
        Shopify is connected, but no products are visible. Check Storefront API product-read scope.
      </CardContent></Card>
    );
  }
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {data.items.map((p) => (
        <Card key={p.id} className="overflow-hidden flex flex-col">
          {p.image_url && <div className="aspect-square bg-muted"><img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /></div>}
          <CardContent className="p-4 flex-1 flex flex-col">
            <h3 className="font-medium leading-tight">{p.title}</h3>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-base font-semibold tabular-nums">
                {p.currency === 'USD' ? formatCents(p.price_cents) : `${(p.price_cents / 100).toFixed(2)} ${p.currency}`}
              </span>
              <Button asChild size="sm" variant="outline">
                <a href={p.online_store_url} target="_blank" rel="noreferrer">Open <ExternalLink className="ml-1 h-3.5 w-3.5" /></a>
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Modals ───────────────────────────────────────────────────────────────

function ProductEditor({ initial, onClose, onSaved }: {
  initial: Product | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { getToken } = useAuth();
  const [name, setName] = useState(initial?.name ?? '');
  const [sku, setSku] = useState(initial?.sku ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [priceDollars, setPriceDollars] = useState(initial ? (initial.price_cents / 100).toFixed(2) : '');
  const [stockQty, setStockQty] = useState(initial ? String(initial.stock_qty) : '0');
  const [imageUrl, setImageUrl] = useState(initial?.image_url ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const cents = dollarsToCents(priceDollars);
    if (!Number.isFinite(cents) || cents < 0) { setErr('Invalid price'); return; }
    setBusy(true); setErr(null);
    try {
      const token = await getToken();
      const body = {
        name: name.trim(),
        sku: sku.trim() || null,
        description: description.trim() || null,
        price_cents: cents,
        stock_qty: Math.max(0, parseInt(stockQty || '0', 10) || 0),
        image_url: imageUrl.trim() || null,
      };
      if (initial) {
        await apiCall(`/v1/products/${initial.id}`, { method: 'PATCH', bearerToken: token, body });
      } else {
        await apiCall('/v1/products', { method: 'POST', bearerToken: token, body });
      }
      onSaved();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed'); }
    finally { setBusy(false); }
  }

  const inp = 'w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  return (
    <Modal onClose={onClose} title={initial ? 'Edit product' : 'New product'}>
      <form onSubmit={submit} className="p-5 space-y-3">
        <LabeledField label="Name *">
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={160} className={inp} />
        </LabeledField>
        <div className="grid grid-cols-2 gap-3">
          <LabeledField label="SKU">
            <input value={sku} onChange={(e) => setSku(e.target.value)} maxLength={80} className={inp} placeholder="MSP-001" />
          </LabeledField>
          <LabeledField label="Stock qty">
            <input type="number" min="0" value={stockQty} onChange={(e) => setStockQty(e.target.value)} className={inp} />
          </LabeledField>
        </div>
        <LabeledField label="Price (USD)">
          <input value={priceDollars} onChange={(e) => setPriceDollars(e.target.value)} required placeholder="19.99" className={inp} />
        </LabeledField>
        <LabeledField label="Image URL (optional)">
          <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className={inp} placeholder="https://…" />
        </LabeledField>
        <LabeledField label="Description">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} className={inp + ' resize-none'} />
        </LabeledField>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!name.trim() || busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{initial ? 'Save' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function SellDialog({ product, onClose, onSold }: {
  product: Product;
  onClose: () => void;
  onSold: () => void;
}) {
  const { getToken } = useAuth();
  const [qty, setQty] = useState('1');
  const [buyerName, setBuyerName] = useState('');
  const [method, setMethod] = useState<'cash' | 'card' | 'stripe' | 'other'>('cash');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const qtyN = Math.max(1, parseInt(qty || '1', 10) || 1);
  const total = product.price_cents * qtyN;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const token = await getToken();
      await apiCall('/v1/store_sales', {
        method: 'POST', bearerToken: token,
        body: {
          product_id: product.id,
          qty: qtyN,
          payment_method: method,
          buyer_name: buyerName.trim() || null,
          notes: notes.trim() || null,
        },
      });
      onSold();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed'); }
    finally { setBusy(false); }
  }

  const inp = 'w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  return (
    <Modal onClose={onClose} title={`Sell ${product.name}`}>
      <form onSubmit={submit} className="p-5 space-y-3">
        <div className="rounded border bg-muted/40 p-3 text-sm flex justify-between">
          <span className="text-muted-foreground">Unit price</span>
          <span className="font-medium tabular-nums">{formatCents(product.price_cents)}</span>
        </div>
        <LabeledField label="Quantity">
          <input type="number" min="1" max={String(product.stock_qty)} value={qty} onChange={(e) => setQty(e.target.value)} className={inp} />
          <p className="text-xs text-muted-foreground mt-1">{product.stock_qty} available</p>
        </LabeledField>
        <LabeledField label="Buyer name (optional)">
          <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} maxLength={160} className={inp} placeholder="Walk-in visitor" />
        </LabeledField>
        <LabeledField label="Payment method">
          <select value={method} onChange={(e) => setMethod(e.target.value as typeof method)} className={inp}>
            <option value="cash">Cash</option>
            <option value="card">Card (manual)</option>
            <option value="stripe">Stripe</option>
            <option value="other">Other</option>
          </select>
        </LabeledField>
        <LabeledField label="Notes">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} className={inp} />
        </LabeledField>
        <div className="border-t pt-3 flex justify-between text-base">
          <span>Total</span>
          <span className="font-semibold tabular-nums">{formatCents(total)}</span>
        </div>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || qtyN > product.stock_qty}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Record sale
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-card text-card-foreground rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function LabeledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      {children}
    </div>
  );
}
